import React, { useState } from 'react';
import { Search, Package, Check, Plus, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { ComparisonDock, MAX_COMPARISON_SELECTION as MAX_SELECTION } from './ComparisonDock';
import type { ProductSummary } from '../../types/productComparer';



interface ProductComparerSearchProps {
    onCompare: (items: ProductSummary[]) => void;
    onSearch: (query: string) => Promise<ProductSummary[]>;
    onOpenSettings: () => void;
}

export function ProductComparerSearch({ onCompare, onSearch, onOpenSettings }: ProductComparerSearchProps) {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState<ProductSummary[]>([]);
    const [selectedItems, setSelectedItems] = useState<ProductSummary[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [hasSearched, setHasSearched] = useState(false);

    const handleSearch = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!query.trim()) return;

        setLoading(true);
        setError(null);
        setHasSearched(true);

        try {
            const items = await onSearch(query.trim());
            setResults(items);
            if (items.length === 0) {
                setError('No items found matching your search.');
            }
        } catch {
            setError('Failed to search for items. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const toggleSelection = (item: ProductSummary) => {
        if (selectedItems.find(i => i.entityId === item.entityId)) {
            setSelectedItems(prev => prev.filter(i => i.entityId !== item.entityId));
        } else if (selectedItems.length < MAX_SELECTION) {
            setSelectedItems(prev => [...prev, item]);
        }
    };

    const isSelected = (item: ProductSummary) =>
        !!selectedItems.find(i => i.entityId === item.entityId);

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="text-center">
                <h2 className="text-2xl font-bold tracking-tight">Product Comparer</h2>
                <p className="text-muted-foreground">
                    Search and select up to {MAX_SELECTION} products to compare side-by-side.
                </p>
            </div>

            {/* Search Form */}
            <form onSubmit={handleSearch} className="mx-auto flex max-w-xl gap-2">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <input
                        type="text"
                        className="flex h-10 w-full rounded-md border border-input bg-background pl-10 pr-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        placeholder="Search by product name..."
                        value={query}
                        onChange={e => setQuery(e.target.value)}
                    />
                </div>
                <Button type="submit" disabled={loading || !query.trim()}>
                    {loading ? 'Searching…' : 'Search'}
                </Button>
            </form>

            {error && (
                <div className="mx-auto flex max-w-xl items-center gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
                    <AlertCircle className="h-4 w-4 shrink-0" />
                    {error}
                </div>
            )}

            {/* Results Grid */}
            {results.length > 0 && (
                <div>
                    <div className="mb-4 flex items-center justify-between">
                        <p className="text-sm text-muted-foreground">
                            {results.length} result{results.length !== 1 ? 's' : ''} found
                        </p>
                    </div>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                        {results.map(item => {
                            const selected = isSelected(item);
                            const disabled = !selected && selectedItems.length >= MAX_SELECTION;
                            return (
                                <div
                                    key={item.entityId}
                                    onClick={() => !disabled && toggleSelection(item)}
                                    className={`group relative flex cursor-pointer flex-col overflow-hidden rounded-lg border-2 bg-card transition-all ${
                                        selected
                                            ? 'border-primary shadow-md ring-1 ring-primary'
                                            : 'border-border hover:border-muted-foreground/40 hover:shadow-md'
                                    } ${disabled ? 'cursor-not-allowed opacity-50 grayscale' : ''}`}
                                >
                                    {/* Image */}
                                    <div className="flex h-36 items-center justify-center border-b bg-muted/30 p-4">
                                        {item.primaryImageUrl ? (
                                            <img
                                                src={item.primaryImageUrl}
                                                alt={item.internalName ?? ''}
                                                className="h-full w-full object-contain transition-transform group-hover:scale-105"
                                                onError={e => {
                                                    (e.target as HTMLImageElement).style.display = 'none';
                                                    (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden');
                                                }}
                                            />
                                        ) : null}
                                        <Package className={`h-10 w-10 ${item.primaryImageUrl ? 'hidden' : ''} text-muted-foreground/30`} />
                                    </div>

                                    {/* Content */}
                                    <div className="flex flex-1 flex-col p-3">
                                        <h3
                                            className="line-clamp-2 text-sm font-semibold leading-tight"
                                            title={item.internalName ?? undefined}
                                        >
                                            {item.internalName ?? `Item ${item.entityId}`}
                                        </h3>
                                        <p className="mt-1 text-xs text-muted-foreground font-mono">
                                            ID: {item.entityId}
                                        </p>

                                        <div className="mt-auto flex justify-end pt-2">
                                            <span
                                                className={`inline-flex h-7 w-7 items-center justify-center rounded-full transition-colors ${
                                                    selected
                                                        ? 'bg-primary text-primary-foreground'
                                                        : 'bg-muted text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary'
                                                }`}
                                            >
                                                {selected ? <Check className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {!hasSearched && results.length === 0 && !loading && !error && (
                <div className="py-16 text-center text-muted-foreground">
                    Start by typing a product name above.
                </div>
            )}

            <ComparisonDock items={selectedItems} onRemove={toggleSelection} onCompare={() => onCompare(selectedItems)} />
        </div>
    );
}
